import { useId, useState } from "react";
import { Download, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useUpdateBannerVisible } from "@/components/common/app-update-banner/update-banner-visibility";
import {
  aboveBottomStrip,
  RAIL_WIDTH_VAR,
} from "@/components/common/bottom-nav/nav-constants";
import {
  FAB_GAP_PX,
  FAB_HEIGHT_PX,
} from "@/components/common/floating-action-button/floating-action-button";
import { InstallInstructionsDialog } from "@/components/common/install-instructions-dialog/install-instructions-dialog";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { useInstallBannerDismissal } from "./use-install-banner-dismissal";

/** Clear of the floating action buttons: their gap, their height, and 8px. */
const ABOVE_FABS_GAP_PX = FAB_GAP_PX + FAB_HEIGHT_PX + 8;
/** Where there are no FABs (the projects home). */
const HOME_GAP_PX = 16;

/**
 * A dismissible card that offers to install Otus: "Install" where the browser
 * handed over a prompt, "How to install" everywhere else it can be installed.
 * Mounted by the screens that may show it (projects home, project tab pages),
 * so it sits under the same CSS custom properties as the FABs. Yields to
 * AppUpdateBanner. See [[install-app-banner.spec]].
 *
 * `aboveFabs`: inside a project, stand in the lane above the FABs so the card
 * never covers one.
 */
export function InstallAppBanner({ aboveFabs = false }: { aboveFabs?: boolean }) {
  const { platform, isStandalone, installEvent, installed, install } =
    usePwaInstall();
  const updateBannerShowing = useUpdateBannerVisible();
  const { coolingDown, dismiss } = useInstallBannerDismissal();
  const [dialogOpen, setDialogOpen] = useState(false);
  const titleId = useId();

  const eligible =
    !isStandalone && platform !== "unsupported" && !installed && !coolingDown;
  const visible = eligible && !updateBannerShowing;
  const canPrompt = platform === "chromium" && installEvent !== null;

  const handlePrimary = async () => {
    if (!canPrompt) {
      setDialogOpen(true);
      return;
    }
    try {
      // Cancelling the native prompt is an explicit "no": same as dismissing.
      if ((await install()) === "dismissed") dismiss();
    } catch {
      // `prompt()` rejected. The store has dropped the spent event, so the
      // button now reads "How to install".
    }
  };

  return (
    <>
      {visible && (
        <div
          className="pointer-events-none fixed right-0 z-[45] flex justify-center px-4"
          style={{
            bottom: aboveBottomStrip(
              aboveFabs ? ABOVE_FABS_GAP_PX : HOME_GAP_PX,
              0,
            ),
            left: `var(${RAIL_WIDTH_VAR}, 0px)`,
          }}
        >
          <section
            aria-labelledby={titleId}
            className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border border-border bg-card p-3 text-card-foreground shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-200 motion-reduce:animate-none"
          >
            <img
              src="/icon-192x192.png"
              width={40}
              height={40}
              alt=""
              className="shrink-0 rounded-lg"
            />
            <div className="min-w-0 flex-1">
              <p id={titleId} className="truncate text-sm font-semibold">
                Install Otus
              </p>
              <p className="line-clamp-2 text-xs text-muted-foreground">
                Quick access from your home screen
              </p>
            </div>
            <Button type="button" size="sm" onClick={handlePrimary}>
              {canPrompt ? (
                <>
                  <Download className="h-4 w-4" />
                  Install
                </>
              ) : (
                "How to install"
              )}
            </Button>
            <button
              type="button"
              aria-label="Dismiss install banner"
              onClick={dismiss}
              className="-mr-1 flex size-10 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <X className="h-4 w-4" />
            </button>
          </section>
        </div>
      )}

      {/* Outside the visibility check: the update bar stepping in must not
          close instructions the user is reading. */}
      <InstallInstructionsDialog
        open={dialogOpen && eligible}
        onOpenChange={setDialogOpen}
        platform={platform}
      />
    </>
  );
}
